import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Grid,
  Image,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  ExportOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
  ShopOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchListings } from '../store/slices/listingsSlice';
import { fetchStores } from '../store/slices/storesSlice';
import PageShell from '../components/Layout/PageShell';
import type { Listing } from '../types';

const { Text } = Typography;

type SortPreset =
  | 'createdAt:desc'
  | 'createdAt:asc'
  | 'title:asc'
  | 'title:desc'
  | 'price:asc'
  | 'price:desc'
  | 'status:asc'
  | 'status:desc';

const SORT_OPTIONS: { value: SortPreset; label: string }[] = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'title:asc', label: 'Title A–Z' },
  { value: 'title:desc', label: 'Title Z–A' },
  { value: 'price:desc', label: 'Price high → low' },
  { value: 'price:asc', label: 'Price low → high' },
  { value: 'status:asc', label: 'Status A–Z' },
  { value: 'status:desc', label: 'Status Z–A' },
];

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'sold', label: 'Sold' },
  { value: 'expired', label: 'Expired' },
  { value: 'flagged', label: 'Flagged' },
];

function listingStatusTag(status: string | undefined, compact?: boolean) {
  if (!status) return <Tag color="default">—</Tag>;
  const label = status.replace(/_/g, ' ').toUpperCase();
  const color =
    status === 'active'
      ? 'green'
      : status === 'draft'
        ? 'default'
        : status === 'inactive'
          ? 'orange'
          : status === 'sold'
            ? 'blue'
            : status === 'expired'
              ? 'default'
              : status === 'flagged'
                ? 'red'
                : 'default';
  return (
    <Tag color={color} className={compact ? 'm-0 max-w-full truncate px-1.5 py-0 text-[10px] leading-4' : undefined}>
      {label}
    </Tag>
  );
}

function formatListingPrice(listing: Listing): string {
  const p = listing.price;
  const amount = typeof p === 'number' ? p : (p as { amount?: number })?.amount ?? 0;
  const currency =
    typeof p === 'object' && p && 'currency' in p ? (p as { currency?: string }).currency || 'INR' : 'INR';
  if (currency === 'INR') {
    return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  return `${currency} ${Number(amount).toFixed(2)}`;
}

function listingImageUrl(listing: Listing): string | undefined {
  const imgs = listing.images;
  if (!Array.isArray(imgs) || imgs.length === 0) return undefined;
  const first = imgs[0];
  return typeof first === 'string' ? first : first?.url;
}

function categoryName(listing: Listing): string {
  const c = listing.category;
  if (c && typeof c === 'object' && 'name' in c && typeof (c as { name?: string }).name === 'string') {
    return (c as { name: string }).name;
  }
  return '—';
}

function storeNameFromListing(listing: Listing): string {
  const s = listing.store;
  if (s && typeof s === 'object' && 'storeName' in s && typeof (s as { storeName?: string }).storeName === 'string') {
    return (s as { storeName: string }).storeName;
  }
  return '—';
}

function publicListingUrl(slug: string): string {
  const raw = import.meta.env.VITE_MARKETPLACE_URL || 'http://localhost:3000';
  const base = raw.replace(/\/$/, '');
  return `${base}/listings/${encodeURIComponent(slug)}`;
}

const Listings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { listings, loading, pagination } = useAppSelector((state) => state.listings);
  const { stores: vendorStores } = useAppSelector((state) => state.stores);

  const screens = Grid.useBreakpoint();
  const useCompactFilters = screens.md === false;
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [sortPreset, setSortPreset] = useState<SortPreset>('createdAt:desc');

  useEffect(() => {
    if (!vendorStores.length) {
      dispatch(fetchStores({ page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' }));
    }
  }, [dispatch, vendorStores.length]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchText]);

  const prevDebouncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevDebouncedRef.current === null) {
      prevDebouncedRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedRef.current !== debouncedSearch) {
      prevDebouncedRef.current = debouncedSearch;
      setPage(1);
    }
  }, [debouncedSearch]);

  const [sortBy, sortOrder] = useMemo(() => {
    const [a, b] = sortPreset.split(':') as [
      'createdAt' | 'title' | 'price' | 'status',
      'asc' | 'desc',
    ];
    return [a, b] as const;
  }, [sortPreset]);

  useEffect(() => {
    dispatch(
      fetchListings({
        page,
        limit: pageSize,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        store: storeFilter || undefined,
        sortBy,
        sortOrder,
      })
    );
  }, [dispatch, page, pageSize, debouncedSearch, statusFilter, storeFilter, sortBy, sortOrder]);

  const onStatusChange = useCallback((v: string) => {
    setStatusFilter(v);
    setPage(1);
  }, []);

  const onStoreChange = useCallback((v: string) => {
    setStoreFilter(v);
    setPage(1);
  }, []);

  const onSortChange = useCallback((v: SortPreset) => {
    setSortPreset(v);
    setPage(1);
  }, []);

  const onPaginationChange = useCallback(
    (p: number, ps?: number) => {
      const nextSize = ps ?? pageSize;
      if (nextSize !== pageSize) {
        setPageSize(nextSize);
        setPage(1);
      } else {
        setPage(p);
      }
    },
    [pageSize]
  );

  const total = pagination?.totalItems ?? 0;

  const storeFilterOptions = useMemo(
    () =>
      vendorStores.map((s) => ({
        value: s._id,
        label: s.storeName || 'Store',
      })),
    [vendorStores]
  );

  const openListingPublic = useCallback(
    (listing: Listing) => {
      if (!listing.slug) {
        navigate(`/listings/${listing._id}/edit`);
        return;
      }
      if (listing.status === 'active' && listing.isApproved) {
        window.open(publicListingUrl(listing.slug), '_blank', 'noopener,noreferrer');
      } else {
        navigate(`/listings/${listing._id}/edit`);
      }
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        title: 'Listing',
        key: 'listing',
        width: 280,
        render: (_: unknown, record: Listing) => {
          const src = listingImageUrl(record);
          return (
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-100 bg-slate-100">
                {src ? (
                  <Image src={src} alt="" width={44} height={44} className="!h-11 !w-11 object-cover" preview={false} />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-slate-400">
                    {(record.title || '?').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{record.title}</div>
                <div className="text-xs text-slate-500 truncate">
                  {categoryName(record)} · {storeNameFromListing(record)}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: 'Price',
        key: 'price',
        width: 120,
        render: (_: unknown, record: Listing) => (
          <span className="whitespace-nowrap font-medium text-slate-800">{formatListingPrice(record)}</span>
        ),
      },
      {
        title: 'Type',
        key: 'listingType',
        width: 100,
        render: (_: unknown, record: Listing) => (
          <Text type="secondary" className="text-xs capitalize">
            {record.listingType || '—'}
          </Text>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: string) => listingStatusTag(status, false),
      },
      {
        title: 'Views',
        key: 'views',
        width: 72,
        render: (_: unknown, record: Listing) => record.stats?.views ?? record.views ?? 0,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 180,
        render: (_: unknown, record: Listing) => (
          <Space size="small" wrap>
            <Button type="link" className="!px-1" icon={<ExportOutlined />} onClick={() => openListingPublic(record)}>
              View
            </Button>
            <Button
              type="link"
              className="!px-1"
              icon={<EditOutlined />}
              onClick={() => navigate(`/listings/${record._id}/edit`)}
            >
              Edit
            </Button>
          </Space>
        ),
      },
    ],
    [navigate, openListingPublic]
  );

  const filterActiveCount = (statusFilter ? 1 : 0) + (storeFilter ? 1 : 0);

  return (
    <PageShell
      title="My listings"
      description="Search your catalog, filter by store or status, and open the marketplace preview when a listing is live."
      titleEnd={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/listings/create')}
          className="w-full min-[480px]:w-auto border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white hover:!text-white shadow-md transition-shadow hover:shadow-lg"
        >
          Create listing
        </Button>
      }
    >
      <Card className="vendor-card-flush border border-slate-200/80 shadow-sm bg-white rounded-xl overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50 px-3 py-2 sm:px-4">
          {useCompactFilters ? (
            <>
              <div className="flex w-full min-w-0 items-stretch gap-2">
                <Input
                  allowClear
                  size="middle"
                  placeholder="Search titles…"
                  prefix={<SearchOutlined className="text-slate-400" />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg"
                />
                <Badge dot={filterActiveCount > 0} className="shrink-0">
                  <Button
                    type="default"
                    size="middle"
                    aria-label="Open sort and filter"
                    icon={<FilterOutlined />}
                    onClick={() => setFiltersDrawerOpen(true)}
                    className="border-slate-200 text-slate-700"
                  >
                    <span className="hidden min-[380px]:inline">Filters</span>
                  </Button>
                </Badge>
              </div>
              <Drawer
                title="Sort & filter"
                placement="bottom"
                height="auto"
                open={filtersDrawerOpen}
                onClose={() => setFiltersDrawerOpen(false)}
                rootClassName="listings-filter-drawer"
                styles={{ body: { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' } }}
              >
                <div className="flex flex-col gap-4">
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Sort by
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      value={sortPreset}
                      onChange={(v) => onSortChange(v as SortPreset)}
                      options={SORT_OPTIONS}
                      showSearch
                      optionFilterProp="label"
                      suffixIcon={<SortAscendingOutlined className="text-slate-500" />}
                    />
                  </div>
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      placeholder="All statuses"
                      allowClear
                      value={statusFilter || undefined}
                      onChange={(v) => onStatusChange(typeof v === 'string' ? v : '')}
                      options={STATUS_FILTER_OPTIONS}
                      showSearch
                      optionFilterProp="label"
                    />
                  </div>
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Store
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      placeholder="All stores"
                      allowClear
                      value={storeFilter || undefined}
                      onChange={(v) => onStoreChange(typeof v === 'string' ? v : '')}
                      options={storeFilterOptions}
                      showSearch
                      optionFilterProp="label"
                      suffixIcon={<ShopOutlined className="text-slate-500" />}
                    />
                  </div>
                  <Button type="primary" block size="large" onClick={() => setFiltersDrawerOpen(false)}>
                    Done
                  </Button>
                </div>
              </Drawer>
            </>
          ) : (
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24} sm={12} md={8} lg={7} xl={8}>
                <Input
                  allowClear
                  size="middle"
                  placeholder="Search listing titles"
                  prefix={<SearchOutlined className="text-slate-400" />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="rounded-lg"
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Sort by"
                  value={sortPreset}
                  onChange={onSortChange}
                  options={SORT_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                  suffixIcon={<SortAscendingOutlined className="text-slate-500" />}
                />
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Status"
                  allowClear
                  value={statusFilter || undefined}
                  onChange={(v) => onStatusChange(typeof v === 'string' ? v : '')}
                  options={STATUS_FILTER_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
              <Col xs={24} sm={12} md={4} lg={7} xl={6}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Store"
                  allowClear
                  value={storeFilter || undefined}
                  onChange={(v) => onStoreChange(typeof v === 'string' ? v : '')}
                  options={storeFilterOptions}
                  showSearch
                  optionFilterProp="label"
                  suffixIcon={<ShopOutlined className="text-slate-500" />}
                />
              </Col>
            </Row>
          )}
          <Text type="secondary" className="mt-1.5 block text-[11px] leading-snug sm:text-xs">
            {total === 0 && !loading
              ? 'No listings match your filters.'
              : loading
                ? 'Updating results…'
                : `${total} listing${total === 1 ? '' : 's'} · this page ${listings.length}`}
          </Text>
        </div>

        <div className="hidden lg:block px-2 py-2 sm:px-3">
          <Table<Listing>
            columns={columns}
            dataSource={listings}
            loading={loading}
            rowKey="_id"
            pagination={false}
            size="small"
            className="modern-table"
            scroll={{ x: 960 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No listings yet — create one from a verified store."
                />
              ),
            }}
          />
        </div>

        <div className="lg:hidden px-2 pb-2 pt-1 sm:px-3 sm:pb-3 sm:pt-2">
          {loading && listings.length === 0 ? (
            <div className="space-y-2.5 py-1">
              {[0, 1, 2].map((i) => (
                <Card key={i} className="rounded-xl border border-slate-200/80 shadow-sm" size="small">
                  <div className="flex flex-col gap-3">
                    <Skeleton.Avatar active size={52} shape="square" className="!rounded-xl" />
                    <Skeleton active title={{ width: '90%' }} paragraph={{ rows: 2 }} />
                  </div>
                </Card>
              ))}
            </div>
          ) : listings.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No listings match your filters."
              className="py-8"
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/listings/create')}>
                Create listing
              </Button>
            </Empty>
          ) : (
            <Spin spinning={loading} size="small" className="w-full [&_.ant-spin-nested-loading]:min-h-[4rem]">
              <ul className="m-0 list-none space-y-2.5 p-0 sm:space-y-3">
                {listings.map((listing) => {
                  const img = listingImageUrl(listing);
                  return (
                    <li key={listing._id}>
                      <Card
                        size="small"
                        className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-shadow hover:shadow-md"
                      >
                        <div className="flex flex-col gap-3">
                          <div className="h-[52px] w-[52px] shrink-0 self-start overflow-hidden rounded-xl border border-slate-100 bg-slate-100">
                            {img ? (
                              <Image
                                src={img}
                                alt=""
                                width={52}
                                height={52}
                                className="!h-[52px] !w-[52px] object-cover"
                                preview={false}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-[#0171BF]">
                                {(listing.title || '?').charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 w-full">
                            <Text strong className="break-words line-clamp-4 text-sm leading-snug text-slate-900">
                              {listing.title}
                            </Text>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                              {listingStatusTag(listing.status, true)}
                              <Text className="text-[11px] font-semibold text-slate-800 sm:text-xs">
                                {formatListingPrice(listing)}
                              </Text>
                            </div>
                            <Text type="secondary" className="mt-0.5 block text-[11px] leading-snug sm:text-xs">
                              {categoryName(listing)}
                              <span className="text-slate-300"> · </span>
                              {storeNameFromListing(listing)}
                            </Text>
                            <Text type="secondary" className="mt-0.5 block text-[11px]">
                              {listing.listingType ? `${listing.listingType} · ` : ''}
                              {listing.stats?.views ?? listing.views ?? 0} views
                            </Text>
                          </div>
                        </div>
                        <Divider className="!my-2.5 border-slate-100" />
                        <div className="flex gap-2">
                          <Button
                            size="middle"
                            className="min-h-9 flex-1 border-slate-200 font-medium text-slate-800"
                            icon={<ExportOutlined />}
                            onClick={() => openListingPublic(listing)}
                          >
                            View
                          </Button>
                          <Button
                            type="primary"
                            size="middle"
                            className="min-h-9 flex-1 border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] font-medium !text-white shadow-sm hover:!text-white hover:shadow-md"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/listings/${listing._id}/edit`)}
                          >
                            Edit
                          </Button>
                        </div>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            </Spin>
          )}
        </div>

        {total > 0 ? (
          <div className="flex flex-col gap-2 border-t border-slate-100 bg-white px-2 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2 sm:px-3 sm:py-2.5">
            <Text type="secondary" className="block text-center text-xs sm:text-left">
              {`${Math.min((page - 1) * pageSize + 1, total)}–${Math.min(page * pageSize, total)} of ${total}`}
            </Text>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              onChange={onPaginationChange}
              size="small"
              showTotal={undefined}
              responsive
              className="listings-pagination !m-0 flex w-full justify-center sm:w-auto sm:justify-end text-xs sm:text-sm"
            />
          </div>
        ) : null}
      </Card>
    </PageShell>
  );
};

export default Listings;
